import { useCallback, useMemo, useState } from 'react';
import { ClientsFilter } from '../../model/types';
import { filterAndSortSminexClubClients } from '../filterAndSortSminexClubClients';
import { mapWsClientsToClients } from '../utils/mapWsClientsToClients';
import { useSminexClubClientsByEvent } from '@shared/api/sminex-club-clients-ws';
import { refetchEventPageWs } from '@shared/api/event-page-ws';
import { useFiltersProjects } from '@shared/api/filters-ws';

export const useSminexClubEventParticipantsFromWs = (eventId: number) => {
  const { clients: wsClients, status } = useSminexClubClientsByEvent();
  const { projects } = useFiltersProjects();
  const [searchString, setSearchString] = useState('');
  const [orderBy, setOrderBy] = useState<string[] | null>(null);
  const [clientsFilter, setClientsFilter] = useState<ClientsFilter>({});

  const allClients = useMemo(() => mapWsClientsToClients(wsClients, projects), [wsClients, projects]);

  const clients = useMemo(
    () => filterAndSortSminexClubClients(allClients, searchString, clientsFilter, orderBy),
    [allClients, searchString, orderBy, clientsFilter]
  );

  const handleSearchChange = useCallback((value: string) => {
    setSearchString(value);
  }, []);

  const handleOrderChange = useCallback((value: string[] | null) => {
    setOrderBy(value);
  }, []);

  const handleFilterChange = useCallback((value: ClientsFilter) => {
    setClientsFilter(value);
  }, []);

  const refetch = useCallback(() => {
    if (!eventId) return;

    refetchEventPageWs(eventId);
  }, [eventId]);

  const fetching = status !== 'ready' && status !== 'error';
  const error = status === 'error' ? new Error('SminexClubClientsByEvent failed') : undefined;

  const ticketInfo = useMemo(() => {
    const total = allClients.length;
    const countAccepted = allClients.filter(
      (client) => client.ticket.sminexClubInvitation?.toUpperCase() === 'CONFIRMED'
    ).length;
    const countDeclined = allClients.filter(
      (client) => client.ticket.sminexClubInvitation?.toUpperCase() === 'REJECTED'
    ).length;
    const checked = allClients.filter((client) => client.ticket.checked).length;

    return { total, countAccepted, countDeclined, checked };
  }, [allClients]);

  return {
    clients,
    allClients,
    fetching,
    error,
    handleSearchChange,
    handleOrderChange,
    handleFilterChange,
    ticketInfo,
    refetch,
  };
};
